import type { CompletenessReport, CompletenessShift } from "./completeness";

/** Why a run produced no page, as the harness inside the frame reports it. */
export type HarnessReportedFailureKind =
	| "syntax"
	| "runtime"
	| "timeout"
	| "bad-output"
	| "too-large"
	/** The browser lacks what the completeness check needs (`Element.checkVisibility`). No template can fix it. */
	| "unsupported";

/**
 * What the host sees from outside the frame: the harness never said it was ready (`not-ready`), or the frame
 * left the harness page (`navigated`) and was replaced. Neither is the template's doing.
 */
export type HostDetectedFailureKind = "not-ready" | "navigated";

export type HarnessFailureKind = HarnessReportedFailureKind | HostDetectedFailureKind;

export type HarnessLimits = {
	maxSourceBytes: number;
	maxOutputBytes: number;
	renderTimeoutMs: number;
};

/** What one frame's harness starts with. `token` is a per-frame secret every message back carries. */
export type HarnessSetup = {
	limits: HarnessLimits;
	token: string;
};

const KILOBYTE = 1024;
const MEGABYTE = KILOBYTE * KILOBYTE;
const TEMPLATE_SOURCE_LIMIT_KB = 200;
const RENDERED_HTML_LIMIT_MB = 5;
const RENDER_TIMEOUT_MS = 5000;

export const HARNESS_LIMITS: HarnessLimits = {
	maxSourceBytes: TEMPLATE_SOURCE_LIMIT_KB * KILOBYTE,
	maxOutputBytes: RENDERED_HTML_LIMIT_MB * MEGABYTE,
	renderTimeoutMs: RENDER_TIMEOUT_MS,
};

/** How long the host waits for a new frame's `ready` before reporting `not-ready`. */
export const HARNESS_READY_TIMEOUT_MS = 10_000;

/** Host → iframe. A new `runId` per run; replies for older runs are dropped. */
export type HarnessRenderMessage = {
	type: "render";
	runId: number;
	source: string;
	data: unknown;
	/** Items that must each be visibly on the page, matched by `data-shift-id`. */
	required: CompletenessShift[];
	dir: "ltr" | "rtl";
};

/** `title` becomes the page's title while it prints: browsers suggest it as the file name when saving as PDF. */
export type HarnessPrintMessage = { type: "print"; title: string };

export type HostToHarnessMessage = HarnessRenderMessage | HarnessPrintMessage;

export type HarnessRenderedMessage = {
	type: "rendered";
	runId: number;
	report: CompletenessReport;
	renderMs: number;
	heightPx: number;
	/** The printable width the page was laid out and checked at, from its `@page` size and margins. */
	widthPx: number;
};
export type HarnessFailedMessage = { type: "failed"; runId: number; kind: HarnessReportedFailureKind; message: string };

/**
 * Iframe → host. `leaving` is sent as the harness page unloads, i.e. when the frame navigates away. `print-ended` once
 * a print request is over: the dialog closed, or the print was refused (no passing render).
 */
export type HarnessToHostMessage = { type: "ready" } | { type: "leaving" } | { type: "print-ended" } | HarnessRenderedMessage | HarnessFailedMessage;

/** The messages that aren't about a run. */
type HarnessFrameEvent = Extract<HarnessToHostMessage, { type: "ready" | "leaving" | "print-ended" }>;

const isFrameEvent = (message: HarnessToHostMessage): message is HarnessFrameEvent =>
	message.type === "ready" || message.type === "leaving" || message.type === "print-ended";

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;
const isStringArray = (value: unknown): value is string[] => Array.isArray(value) && value.every((item) => typeof item === "string");

const isReportedFailureKind = (value: unknown): value is HarnessReportedFailureKind =>
	value === "syntax" || value === "runtime" || value === "timeout" || value === "bad-output" || value === "too-large" || value === "unsupported";

const isReport = (value: unknown): value is CompletenessReport =>
	isRecord(value) && typeof value.ok === "boolean" && isStringArray(value.missing) && isStringArray(value.unknownIds) && typeof value.mediaRules === "boolean";

const parseRunResult = (payload: Record<string, unknown>, runId: number): HarnessToHostMessage | null => {
	const { type, report, renderMs, heightPx, widthPx, kind, message } = payload;
	if (type === "rendered" && isReport(report) && typeof renderMs === "number" && typeof heightPx === "number" && typeof widthPx === "number") {
		return { type, runId, report, renderMs, heightPx, widthPx };
	}
	if (type === "failed" && isReportedFailureKind(kind) && typeof message === "string") return { type, runId, kind, message };
	return null;
};

const parsePayload = (payload: unknown, token: string): HarnessToHostMessage | null => {
	if (!isRecord(payload) || payload.token !== token) return null;
	if (payload.type === "ready" || payload.type === "leaving" || payload.type === "print-ended") return { type: payload.type };
	return typeof payload.runId === "number" ? parseRunResult(payload, payload.runId) : null;
};

/**
 * Accepts a message only from our own iframe: sent by its window, from the opaque origin a sandbox without
 * `allow-same-origin` has ("null"), carrying the frame's secret `token`, shaped like the protocol, and — for
 * run results — for the run the host is waiting on. Anything else returns null and is ignored.
 *
 * The token is what tells our harness apart from a page the frame navigated to: that page has the same
 * window and the same "null" origin, but never saw the token (it lives only in the harness's own script).
 */
export const acceptHarnessMessage = (
	event: MessageEvent<unknown>,
	frameWindow: Window | null,
	currentRunId: number,
	token: string,
): HarnessToHostMessage | null => {
	if (frameWindow === null || event.source !== frameWindow || event.origin !== "null") return null;
	const message = parsePayload(event.data, token);
	if (message === null || isFrameEvent(message)) return message;
	return message.runId === currentRunId ? message : null;
};
